import React, { useState, useEffect } from 'react';
import Header from '../../layouts/Header';
import { useNavigate } from 'react-router-dom';
import {
    Row, Col, Table, Button, Form, Offcanvas, InputGroup
} from 'react-bootstrap';
import {
    PlusLg, PencilSquare, Trash3, Search, CalendarEvent,
    ChevronLeft, ChevronRight, ArrowLeft, Clock, Funnel, Tag, ShopWindow
} from 'react-bootstrap-icons';
import { AlertCircle, Loader2 } from 'lucide-react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axiosInstance from '../../api/axios';
import { toast } from 'react-toastify';
import OutletSelect from '../../components/OutletSelect'; // adjust path as needed

// ─── Constants ────────────────────────────────────────────────────────────────

const EVENT_NAME_OPTIONS = [
    { value: 'CATEGORY_ACTION',            label: 'Category Action' },
    { value: 'PRODUCT_ACTION',             label: 'Product Action' },
    { value: 'ADVERTISEMENT_ACTION',       label: 'Advertisement Action' },
    { value: 'POPUP_ADVERTISEMENT_ACTION', label: 'Popup Advertisement Action' },
    { value: 'OUTLET_ACTION',              label: 'Outlet Action' },
];

const EVENT_TYPE_COLORS = {
    CATEGORY_ACTION:            'primary',
    PRODUCT_ACTION:             'success',
    ADVERTISEMENT_ACTION:       'info',
    POPUP_ADVERTISEMENT_ACTION: 'warning',
    OUTLET_ACTION:              'secondary',
};

const APPLICABLE_FIELD_MAP = {
    CATEGORY_ACTION:            'applicableCategory',
    PRODUCT_ACTION:             'applicableProduct',
    ADVERTISEMENT_ACTION:       'applicableAdvertisement',
    POPUP_ADVERTISEMENT_ACTION: 'applicablePopupAdvertisement',
    OUTLET_ACTION:              'applicableOutlet',
};

const defaultForm = {
    eventDate:                    null,   // Date object for react-datepicker
    eventTime:                    '',
    eventName:                    'CATEGORY_ACTION',
    eventStatus:                  'ACTIVE',
    applicableCategory:           null,   // { value, label } for react-select
    applicableProduct:            null,
    applicableAdvertisement:      null,
    applicablePopupAdvertisement: null,
    applicableOutlet:             null,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const extractId = (val) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'object') return val._id || '';
    return '';
};

const extractName = (val) => {
    if (!val) return '—';
    if (typeof val === 'string') return val;
    if (typeof val === 'object') return val.name || val.title || val._id || '—';
    return '—';
};

const getApplicableName = (ev) => {
    const val =
        ev.applicableCategory           ||
        ev.applicableProduct            ||
        ev.applicableAdvertisement      ||
        ev.applicablePopupAdvertisement ||
        ev.applicableOutlet             ||
        null;
    return extractName(val);
};

const safe = (val, fallback = '—') => {
    if (val === null || val === undefined) return fallback;
    if (typeof val === 'object') return val.name || val._id || fallback;
    return String(val);
};

// react-select styling tuned to match the dense, bordered inputs used elsewhere
const selectStyles = {
    control: (base, state) => ({
        ...base,
        fontSize: '13px',
        minHeight: '38px',
        borderRadius: '6px',
        borderColor: state.isFocused ? '#4f46e5' : '#e1e4ea',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(79,70,229,0.12)' : 'none',
        '&:hover': { borderColor: '#4f46e5' },
    }),
    option: (base, state) => ({
        ...base,
        fontSize: '13px',
        backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? '#eef0ff' : '#fff',
        color: state.isSelected ? '#fff' : '#1f2937',
    }),
    placeholder: (base) => ({ ...base, fontSize: '13px', color: '#9aa1ac' }),
    singleValue: (base) => ({ ...base, fontSize: '13px' }),
};

// ─── Component ────────────────────────────────────────────────────────────────

const AutomationEvents = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin]     = useState(currentSkin);
    const accessToken         = localStorage.getItem('accessToken');
    const navigate            = useNavigate();
    const isDark              = skin === 'dark';

    // list data
    const [events, setEvents]         = useState([]);
    const [pagination, setPagination] = useState({ totalRecords: 0, totalPages: 1, currentPage: 1, limit: 10 });
    const [loading, setLoading]       = useState(false);
    const [error, setError]           = useState('');

    // dropdown data
    const [categoryOptions, setCategoryOptions]   = useState([]);
    const [productOptions, setProductOptions]     = useState([]);
    const [adOptions, setAdOptions]               = useState([]);
    const [popupAdOptions, setPopupAdOptions]     = useState([]);

    // form / panel
    const [showPanel, setShowPanel]   = useState(false);
    const [isEditing, setIsEditing]   = useState(false);
    const [editId, setEditId]         = useState(null);
    const [form, setForm]             = useState(defaultForm);
    const [formError, setFormError]   = useState('');
    const [saving, setSaving]         = useState(false);
    const [deleting, setDeleting]     = useState(null);

    // filters
    const [search, setSearch]             = useState('');
    const [filterType, setFilterType]     = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [page, setPage]                 = useState(1);

    // ─── Effects ────────────────────────────────────────────────────────────
    useEffect(() => { fetchEvents(page); }, [page]);
    useEffect(() => {
        fetchCategoryOptions();
        fetchProductOptions();
        fetchAdOptions();
        fetchPopupAdOptions();
    }, []);

    // ─── Fetch events ────────────────────────────────────────────────────────
    const fetchEvents = async (pageNum = 1) => {
        setLoading(true);
        setError('');
        try {
            const res = await axiosInstance.get('automation-event', { params: { page: pageNum, limit: 10 } });
            const payload = res.data.data;
            setEvents(payload.dbResponse || []);
            setPagination(payload.pagination || { totalRecords: 0, totalPages: 1, currentPage: 1, limit: 10 });
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Failed to fetch events');
        } finally {
            setLoading(false);
        }
    };

    // ─── Fetch dropdown options ───────────────────────────────────────────────
    const fetchCategoryOptions = async () => {
        try {
            const res = await axiosInstance.get('masters/category/get', {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const list = res?.data?.data?.categories || [];
            setCategoryOptions(list.map(c => ({ value: c._id, label: c.name })));
        } catch (e) { console.error('Category fetch error', e); }
    };

    const fetchProductOptions = async () => {
        try {
            const res  = await axiosInstance.get('product', {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const list = res?.data?.data?.products || [];
            setProductOptions(list.map(p => ({ value: p._id, label: p.name })));
        } catch (e) { console.error('Product fetch error', e); }
    };

    const fetchAdOptions = async () => {
        try {
            const res  = await axiosInstance.get('advertisement', {
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            // adjust path based on your advertisement API response shape
            const list = res?.data?.data?.advertisements?.clickableAdvertisements || [];
            setAdOptions(list.map(a => ({ value: a._id, label: a.title || a.name || a._id })));
        } catch (e) { console.error('Advertisement fetch error', e); }
    };

    const fetchPopupAdOptions = async () => {
        try {
            const res  = await axiosInstance.get('in-app-advertisement', {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const list = res?.data?.data?.inAppAdvertisement || [];
            setPopupAdOptions(list.map(a => ({ value: a._id, label: a.title || a.name || a._id })));
        } catch (e) { console.error('Popup Ad fetch error', e); }
    };

    // ─── Panel helpers ────────────────────────────────────────────────────────
    const openAddPanel = () => {
        setIsEditing(false);
        setEditId(null);
        setForm(defaultForm);
        setFormError('');
        setShowPanel(true);
    };

    const openEditPanel = (ev) => {
        setIsEditing(true);
        setEditId(ev._id);

        // For each populated field, build { value, label } for react-select
        const toOption = (val, fallbackLabel) => {
            if (!val) return null;
            if (typeof val === 'object') return { value: val._id, label: val.name || val.title || val._id };
            return { value: val, label: fallbackLabel || val };
        };

        setForm({
            eventDate:                    ev.eventDate ? new Date(ev.eventDate) : null,
            eventTime:                    ev.eventTime || '',
            eventName:                    ev.eventName || 'CATEGORY_ACTION',
            eventStatus:                  ev.eventStatus || 'ACTIVE',
            applicableCategory:           toOption(ev.applicableCategory),
            applicableProduct:            toOption(ev.applicableProduct),
            applicableAdvertisement:      toOption(ev.applicableAdvertisement),
            applicablePopupAdvertisement: toOption(ev.applicablePopupAdvertisement),
            applicableOutlet:             toOption(ev.applicableOutlet),
        });
        setFormError('');
        setShowPanel(true);
    };

    // ─── Form change handlers ─────────────────────────────────────────────────
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (fieldName, option) => {
        setForm(prev => ({ ...prev, [fieldName]: option }));
    };

    // ─── Build payload ────────────────────────────────────────────────────────
    const buildPayload = () => {
        const applicableKey = APPLICABLE_FIELD_MAP[form.eventName];
        const selectValue   = form[applicableKey];

        // For OutletSelect, value may be a plain option object; for others it's react-select option
        const applicableId = selectValue?.value || '';

        if (!applicableId) {
            setFormError(`Please select the applicable ${form.eventName.replace('_ACTION', '').toLowerCase()}`);
            return null;
        }

        const payload = {
            eventDate: form.eventDate
                ? form.eventDate.toISOString().split('T')[0]   // "YYYY-MM-DD"
                : '',
            eventName:        form.eventName,
            [applicableKey]:  applicableId,
        };

        if (form.eventTime) payload.eventTime = form.eventTime;
        if (isEditing) {
            payload._id          = editId;
            payload.eventStatus  = form.eventStatus;
        }

        return payload;
    };

    // ─── Save ─────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        setFormError('');
        if (!form.eventDate) { setFormError('Event date is required'); return; }
        const payload = buildPayload();
        if (!payload) return;

        setSaving(true);
        try {
            if (isEditing) {
                await axiosInstance.put('automation-event', payload);
            } else {
                await axiosInstance.post('automation-event', payload);
            }
            toast.success(`Event ${isEditing ? 'updated' : 'created'} successfully`, {
                position: 'top-right', autoClose: 3000, theme: 'light',
            });
            setShowPanel(false);
            fetchEvents(page);
        } catch (err) {
            setFormError(err?.response?.data?.message || err.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    // ─── Delete ───────────────────────────────────────────────────────────────
    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this event?')) return;
        setDeleting(id);
        try {
            await axiosInstance.delete('automation-event', { data: { _id: id } });
            toast.success('Event deleted successfully', { position: 'top-right', autoClose: 3000, theme: 'light' });
            if (events.length === 1 && page > 1) {
                setPage(prev => prev - 1);
            } else {
                fetchEvents(page);
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Delete failed', {
                position: 'top-right', autoClose: 3000, theme: 'light',
            });
        } finally {
            setDeleting(null);
        }
    };

    // ─── Filtered list ────────────────────────────────────────────────────────
    const filteredEvents = events.filter(ev => {
        const matchSearch = !search || ev.eventName?.toLowerCase().includes(search.toLowerCase());
        const matchType   = !filterType   || ev.eventName   === filterType;
        const matchStatus = !filterStatus || ev.eventStatus === filterStatus;
        return matchSearch && matchType && matchStatus;
    });

    const totalActive   = events.filter(e => e.eventStatus === 'ACTIVE').length;
    const totalInactive = events.filter(e => e.eventStatus === 'INACTIVE').length;

    // React-select options per event type
    const selectOptionsMap = {
        CATEGORY_ACTION:            categoryOptions,
        PRODUCT_ACTION:             productOptions,
        ADVERTISEMENT_ACTION:       adOptions,
        POPUP_ADVERTISEMENT_ACTION: popupAdOptions,
        OUTLET_ACTION:              [],   // handled by OutletSelect
    };

    const applicableKey = APPLICABLE_FIELD_MAP[form.eventName];

    const applicableLabel =
        form.eventName === 'CATEGORY_ACTION'            ? 'Category' :
        form.eventName === 'PRODUCT_ACTION'             ? 'Product' :
        form.eventName === 'ADVERTISEMENT_ACTION'       ? 'Advertisement' :
        form.eventName === 'POPUP_ADVERTISEMENT_ACTION' ? 'Popup advertisement' :
        'Outlet';

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className={`main main-app p-3 p-lg-4 ae-page ${isDark ? 'ae-dark bg-dark text-light' : 'bg-light'}`}>

                {/* Scoped styles ------------------------------------------------ */}
                <style>{`
                    .ae-page {
                        --ae-border: ${isDark ? '#2c3242' : '#e6e8ec'};
                        --ae-bg-subtle: ${isDark ? '#1b2030' : '#f8f9fb'};
                        --ae-surface: ${isDark ? '#11151f' : '#ffffff'};
                        --ae-text: ${isDark ? '#e7e9ee' : '#1f2430'};
                        --ae-muted: ${isDark ? '#8a93a6' : '#6b7280'};
                        --ae-accent: #4f46e5;
                        --ae-mono: 'SFMono-Regular', 'Roboto Mono', Menlo, Consolas, monospace;
                        font-feature-settings: "tnum" 1;
                    }

                    /* Crumb / page head */
                    .ae-crumb { font-size: 12px; color: var(--ae-muted); }
                    .ae-crumb b { color: var(--ae-text); font-weight: 600; }
                    .ae-page-title { font-size: 19px; font-weight: 700; letter-spacing: -0.01em; color: var(--ae-text); }
                    .ae-page-sub { font-size: 12.5px; color: var(--ae-muted); }

                    /* Stat strip */
                    .ae-stat-bar {
                        display: flex;
                        border: 1px solid var(--ae-border);
                        border-radius: 10px;
                        background: var(--ae-surface);
                        overflow: hidden;
                    }
                    .ae-stat {
                        flex: 1;
                        min-width: 0;
                        padding: 12px 18px;
                        display: flex;
                        flex-direction: column;
                        gap: 4px;
                        border-right: 1px solid var(--ae-border);
                    }
                    .ae-stat:last-child { border-right: none; }
                    .ae-stat-label {
                        font-size: 10.5px;
                        text-transform: uppercase;
                        letter-spacing: 0.08em;
                        font-weight: 700;
                        color: var(--ae-muted);
                        display: flex; align-items: center; gap: 6px;
                    }
                    .ae-stat-value {
                        font-family: var(--ae-mono);
                        font-size: 22px;
                        font-weight: 700;
                        color: var(--ae-text);
                        line-height: 1.1;
                    }

                    /* Card shell */
                    .ae-card {
                        border: 1px solid var(--ae-border);
                        border-radius: 10px;
                        background: var(--ae-surface);
                        overflow: hidden;
                    }

                    /* Toolbar */
                    .ae-toolbar {
                        display: flex; flex-wrap: wrap; gap: 8px; align-items: center;
                        padding: 10px 14px;
                        border-bottom: 1px solid var(--ae-border);
                        background: var(--ae-bg-subtle);
                    }
                    .ae-toolbar .form-control,
                    .ae-toolbar .form-select,
                    .ae-toolbar .input-group-text {
                        font-size: 12.5px;
                        height: 34px;
                        border-color: var(--ae-border);
                        background: var(--ae-surface);
                        color: var(--ae-text);
                    }
                    .ae-toolbar .form-select { padding-top: 0; padding-bottom: 0; }

                    /* Table */
                    .ae-table { margin-bottom: 0; font-size: 12.5px; }
                    .ae-table thead th {
                        font-size: 10.5px;
                        text-transform: uppercase;
                        letter-spacing: 0.07em;
                        font-weight: 700;
                        color: var(--ae-muted);
                        background: var(--ae-bg-subtle);
                        border-bottom: 1px solid var(--ae-border);
                        padding: 9px 14px;
                        white-space: nowrap;
                        position: sticky; top: 0; z-index: 1;
                    }
                    .ae-table tbody td {
                        padding: 9px 14px;
                        vertical-align: middle;
                        border-bottom: 1px solid var(--ae-border);
                        color: var(--ae-text);
                    }
                    .ae-table tbody tr:last-child td { border-bottom: none; }
                    .ae-table tbody tr { transition: background-color .1s ease; }
                    .ae-table tbody tr:hover { background: var(--ae-bg-subtle); }

                    .ae-mono { font-family: var(--ae-mono); font-size: 12px; color: var(--ae-muted); }
                    .ae-row-idx { font-family: var(--ae-mono); font-size: 11.5px; color: var(--ae-muted); }
                    .ae-ellipsis {
                        max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block;
                    }

                    /* Badges & status */
                    .ae-badge {
                        display: inline-flex; align-items: center; gap: 5px;
                        padding: 2px 8px; border-radius: 4px;
                        font-size: 10.5px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
                        white-space: nowrap;
                    }
                    .ae-badge-primary   { background: ${isDark ? '#1f2350' : '#eef0ff'}; color: ${isDark ? '#9aa3ff' : '#4338ca'}; }
                    .ae-badge-success   { background: ${isDark ? '#10301f' : '#ecfdf5'}; color: ${isDark ? '#5fd9a4' : '#047857'}; }
                    .ae-badge-info      { background: ${isDark ? '#102a3d' : '#eff6ff'}; color: ${isDark ? '#7cc1f5' : '#1d4ed8'}; }
                    .ae-badge-warning   { background: ${isDark ? '#3a2c10' : '#fffbeb'}; color: ${isDark ? '#f0c468' : '#b45309'}; }
                    .ae-badge-secondary { background: ${isDark ? '#262c3b' : '#f3f4f6'}; color: ${isDark ? '#aeb6c4' : '#4b5563'}; }
                    .ae-badge-danger    { background: ${isDark ? '#3a1414' : '#fef2f2'}; color: ${isDark ? '#f3a3a3' : '#b91c1c'}; }

                    .ae-status { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; }
                    .ae-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
                    .ae-dot-success { background: #10b981; box-shadow: 0 0 0 3px ${isDark ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.12)'}; }
                    .ae-dot-danger  { background: #ef4444; box-shadow: 0 0 0 3px ${isDark ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.12)'}; }

                    /* Icon buttons */
                    .ae-icon-btn {
                        width: 28px; height: 28px;
                        display: inline-flex; align-items: center; justify-content: center;
                        border-radius: 6px;
                        border: 1px solid var(--ae-border);
                        background: var(--ae-surface);
                        color: var(--ae-muted);
                        transition: all .12s ease;
                    }
                    .ae-icon-btn:hover { color: var(--ae-text); border-color: var(--ae-accent); background: ${isDark ? '#1b1f33' : '#eef0ff'}; }
                    .ae-icon-btn-danger:hover { color: #dc2626; border-color: #fecaca; background: ${isDark ? '#2a1414' : '#fef2f2'}; }
                    .ae-icon-btn:disabled { opacity: .5; cursor: not-allowed; }

                    /* Pagination */
                    .ae-pagination { display: flex; align-items: center; gap: 6px; }
                    .ae-page-btn {
                        min-width: 28px; height: 28px; padding: 0 6px;
                        display: inline-flex; align-items: center; justify-content: center;
                        border-radius: 6px; font-size: 12px; font-weight: 600;
                        border: 1px solid var(--ae-border);
                        background: var(--ae-surface); color: var(--ae-text);
                        transition: all .12s ease;
                    }
                    .ae-page-btn:hover:not(:disabled) { border-color: var(--ae-accent); }
                    .ae-page-btn:disabled { opacity: .4; }
                    .ae-page-btn.active { background: var(--ae-accent); border-color: var(--ae-accent); color: #fff; }

                    /* Offcanvas panel */
                    .ae-section { margin-bottom: 22px; }
                    .ae-section-title {
                        font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.09em; font-weight: 700;
                        color: var(--ae-muted);
                        padding-bottom: 8px; margin-bottom: 14px;
                        border-bottom: 1px solid var(--ae-border);
                    }
                    .ae-field-label {
                        font-size: 12.5px; font-weight: 600; color: var(--ae-text);
                        display: flex; align-items: center; gap: 6px; margin-bottom: 6px;
                    }
                    .ae-field-hint { font-size: 11px; color: var(--ae-muted); margin-top: 5px; }
                    .ae-page .form-control,
                    .ae-page .form-select {
                        font-size: 13px; border-radius: 6px; border-color: var(--ae-border);
                        background: var(--ae-surface); color: var(--ae-text);
                    }
                    .ae-page .form-control:focus,
                    .ae-page .form-select:focus {
                        border-color: var(--ae-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.12);
                    }
                    .ae-page .react-datepicker-wrapper { width: 100%; }

                    .ae-error-box {
                        display: flex; gap: 8px; align-items: flex-start;
                        background: ${isDark ? '#2a1414' : '#fef2f2'};
                        border: 1px solid ${isDark ? '#4a1f1f' : '#fecaca'};
                        color: ${isDark ? '#f3a3a3' : '#b91c1c'};
                        border-radius: 6px; padding: 10px 12px; font-size: 12.5px; margin-bottom: 16px;
                    }

                    .ae-panel-footer {
                        position: sticky; bottom: 0;
                        background: var(--ae-surface);
                        border-top: 1px solid var(--ae-border);
                        margin: 24px -1.5rem 0; padding: 14px 1.5rem 0 1.5rem;
                        display: flex; gap: 8px;
                    }
                    .ae-btn-primary {
                        background: var(--ae-accent); border-color: var(--ae-accent);
                        font-size: 13px; font-weight: 600;
                    }
                    .ae-btn-primary:hover { background: #4338ca; border-color: #4338ca; }
                `}</style>

                {/* Page Header */}
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
                    <div className='w-50'>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                className="d-flex align-items-center gap-1 px-2 py-1 w-25 justify-content-center"
                                style={{ fontSize: '12px' }}
                                onClick={() => navigate('/events')}
                            >
                                <ArrowLeft size={13} /> Back
                            </Button>
                            <span className="ae-crumb ">All events / <b>Automation events</b></span>
                        </div>
                        <h4 className="ae-page-title mb-1">Automation Events</h4>
                        <p className="ae-page-sub mb-0">Scheduled activate / deactivate actions.</p>
                    </div>
                    <Button
                        className="ae-btn-primary d-flex align-items-center gap-2 justify-content-center w-25"
                        onClick={openAddPanel}
                    >
                        <PlusLg size={15} /> Add event
                    </Button>
                </div>

                {/* Summary stat strip */}
                <div className="ae-stat-bar mb-3">
                    <div className="ae-stat">
                        <span className="ae-stat-label">Total records</span>
                        <span className="ae-stat-value">{pagination.totalRecords}</span>
                    </div>
                    <div className="ae-stat">
                        <span className="ae-stat-label"><span className="ae-dot ae-dot-success" />Active</span>
                        <span className="ae-stat-value">{totalActive}</span>
                    </div>
                    <div className="ae-stat">
                        <span className="ae-stat-label"><span className="ae-dot ae-dot-danger" />Inactive</span>
                        <span className="ae-stat-value">{totalInactive}</span>
                    </div>
                    <div className="ae-stat">
                        <span className="ae-stat-label">Page</span>
                        <span className="ae-stat-value">{pagination.currentPage}<span style={{ fontSize: '13px', color: 'var(--ae-muted)', fontWeight: 600 }}> / {pagination.totalPages}</span></span>
                    </div>
                </div>

                {/* Table Card */}
                <div className="ae-card">

                    {/* Filters */}
                    <div className="ae-toolbar">
                        <InputGroup style={{ maxWidth: '240px' }}>
                            <InputGroup.Text className="border-end-0">
                                <Search size={13} />
                            </InputGroup.Text>
                            <Form.Control
                                placeholder="Search by event type…"
                                style={{ borderLeft: 'none' }}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </InputGroup>
                        <InputGroup style={{ width: 'auto' }}>
                            <InputGroup.Text className="border-end-0">
                                <Funnel size={12} />
                            </InputGroup.Text>
                            <Form.Select
                                style={{ width: '180px', borderLeft: 'none' }}
                                value={filterType}
                                onChange={e => setFilterType(e.target.value)}
                            >
                                <option value="">All types</option>
                                {EVENT_NAME_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </Form.Select>
                        </InputGroup>
                        <Form.Select
                            style={{ width: '140px' }}
                            value={filterStatus}
                            onChange={e => setFilterStatus(e.target.value)}
                        >
                            <option value="">All status</option>
                            <option value="ACTIVE">Active</option>
                            <option value="INACTIVE">Inactive</option>
                        </Form.Select>

                        {(search || filterType || filterStatus) && (
                            <Button
                                variant="link"
                                className="p-0 ms-auto"
                                style={{ fontSize: '12px', textDecoration: 'none' }}
                                onClick={() => { setSearch(''); setFilterType(''); setFilterStatus(''); }}
                            >
                                Clear filters
                            </Button>
                        )}
                    </div>

                    {/* Loading */}
                    {loading && (
                        <div className="text-center py-5">
                            <Loader2 size={20} className="ae-mono" style={{ animation: 'spin 0.8s linear infinite', color: 'var(--ae-accent)' }} />
                            <p className="ae-page-sub mt-2 mb-0">Loading events…</p>
                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        </div>
                    )}

                    {/* Error */}
                    {!loading && error && (
                        <div className="d-flex flex-column align-items-center py-5 gap-2" style={{ color: '#dc2626' }}>
                            <AlertCircle size={26} />
                            <p className="mb-1" style={{ fontSize: '13px' }}>{error}</p>
                            <Button size="sm" variant="outline-danger" onClick={() => fetchEvents(page)}>Retry</Button>
                        </div>
                    )}

                    {/* Empty */}
                    {!loading && !error && filteredEvents.length === 0 && (
                        <div className="text-center py-5">
                            <CalendarEvent size={32} style={{ color: 'var(--ae-muted)' }} className="mb-2" />
                            <p className="ae-page-sub mb-0">No automation events match these filters</p>
                        </div>
                    )}

                    {/* Table */}
                    {!loading && !error && filteredEvents.length > 0 && (
                        <div style={{ overflowX: 'auto' }}>
                            <Table responsive hover className="ae-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '44px' }}>#</th>
                                        <th>Event type</th>
                                        <th>Applicable to</th>
                                        <th>Date</th>
                                        <th>Time</th>
                                        <th>Status</th>
                                        <th>Last executed</th>
                                        <th style={{ width: '90px' }} className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredEvents.map((ev, idx) => (
                                        <tr key={ev._id}>
                                            <td className="ae-row-idx">
                                                {String((pagination.currentPage - 1) * pagination.limit + idx + 1).padStart(2, '0')}
                                            </td>
                                            <td>
                                                <span className={`ae-badge ae-badge-${EVENT_TYPE_COLORS[ev.eventName] || 'secondary'}`}>
                                                    {EVENT_NAME_OPTIONS.find(o => o.value === ev.eventName)?.label || safe(ev.eventName)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="ae-ellipsis" title={getApplicableName(ev)}>
                                                    {getApplicableName(ev)}
                                                </span>
                                            </td>
                                            <td className="ae-mono">
                                                {ev.eventDate
                                                    ? new Date(ev.eventDate).toLocaleDateString('en-IN', {
                                                        day: '2-digit', month: 'short', year: 'numeric',
                                                      })
                                                    : '—'}
                                            </td>
                                            <td className="ae-mono">{safe(ev.eventTime)}</td>
                                            <td>
                                                <span className="ae-status">
                                                    <span className={`ae-dot ${ev.eventStatus === 'ACTIVE' ? 'ae-dot-success' : 'ae-dot-danger'}`} />
                                                    {safe(ev.eventStatus)}
                                                </span>
                                            </td>
                                            <td className="ae-mono">
                                                {ev.event_exec_time
                                                    ? new Date(ev.event_exec_time).toLocaleString('en-IN')
                                                    : '—'}
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2 justify-content-end">
                                                    <button
                                                        className="ae-icon-btn"
                                                        onClick={() => openEditPanel(ev)}
                                                        title="Edit"
                                                    >
                                                        <PencilSquare size={13} />
                                                    </button>
                                                    <button
                                                        className="ae-icon-btn ae-icon-btn-danger"
                                                        disabled={deleting === ev._id}
                                                        onClick={() => handleDelete(ev._id)}
                                                        title="Delete"
                                                    >
                                                        {deleting === ev._id
                                                            ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} />
                                                            : <Trash3 size={13} />}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && !error && pagination.totalPages > 1 && (
                        <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderTop: '1px solid var(--ae-border)' }}>
                            <p className="ae-page-sub mb-0">
                                Page {pagination.currentPage} of {pagination.totalPages} · {pagination.totalRecords} total records
                            </p>
                            <div className="ae-pagination">
                                <button
                                    className="ae-page-btn"
                                    disabled={page === 1}
                                    onClick={() => setPage(prev => prev - 1)}
                                >
                                    <ChevronLeft size={13} />
                                </button>
                                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(p => (
                                    <button
                                        key={p}
                                        className={`ae-page-btn ${p === page ? 'active' : ''}`}
                                        onClick={() => setPage(p)}
                                    >
                                        {p}
                                    </button>
                                ))}
                                <button
                                    className="ae-page-btn"
                                    disabled={page === pagination.totalPages}
                                    onClick={() => setPage(prev => prev + 1)}
                                >
                                    <ChevronRight size={13} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Add / Edit Offcanvas ─────────────────────────────────── */}
                <Offcanvas
                    show={showPanel}
                    onHide={() => setShowPanel(false)}
                    placement="end"
                    className={isDark ? 'bg-dark text-light' : ''}
                    style={{ width: '440px' }}
                >
                    <Offcanvas.Header closeButton style={{ borderBottom: '1px solid var(--ae-border)' }}>
                        <div>
                            <Offcanvas.Title style={{ fontSize: '15px', fontWeight: '700' }}>
                                {isEditing ? 'Edit automation event' : 'Add automation event'}
                            </Offcanvas.Title>
                            <p className="ae-page-sub mb-0 mt-1">
                                {isEditing ? 'Update the trigger condition for this event.' : 'Schedule an automatic activate / deactivate action.'}
                            </p>
                        </div>
                    </Offcanvas.Header>
                    <Offcanvas.Body className="d-flex flex-column">
                        <Form className="flex-grow-1 d-flex flex-column">

                            {formError && (
                                <div className="ae-error-box">
                                    <AlertCircle size={15} style={{ marginTop: '1px', flexShrink: 0 }} />
                                    <span>{formError}</span>
                                </div>
                            )}

                            {/* Section: Event configuration */}
                            <div className="ae-section">
                                <div className="ae-section-title">Event configuration</div>

                                <Form.Group className="mb-3">
                                    <Form.Label className="ae-field-label">
                                        <Tag size={13} /> Event type <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        name="eventName"
                                        value={form.eventName}
                                        onChange={handleFormChange}
                                    >
                                        {EVENT_NAME_OPTIONS.map(o => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group>
                                    <Form.Label className="ae-field-label">
                                        <ShopWindow size={13} /> {applicableLabel} <span className="text-danger">*</span>
                                    </Form.Label>

                                    {form.eventName === 'OUTLET_ACTION' ? (
                                        <OutletSelect
                                            value={form.applicableOutlet}
                                            onChange={(option) => handleSelectChange('applicableOutlet', option)}
                                        />
                                    ) : (
                                        <Select
                                            options={selectOptionsMap[form.eventName] || []}
                                            value={form[applicableKey]}
                                            onChange={(option) => handleSelectChange(applicableKey, option)}
                                            placeholder={`Select ${applicableLabel.toLowerCase()}…`}
                                            isClearable
                                            isSearchable
                                            styles={selectStyles}
                                        />
                                    )}
                                </Form.Group>
                            </div>

                            {/* Section: Schedule */}
                            <div className="ae-section">
                                <div className="ae-section-title">Schedule</div>

                                <Form.Group className="mb-3">
                                    <Form.Label className="ae-field-label">
                                        <CalendarEvent size={13} /> Event date <span className="text-danger">*</span>
                                    </Form.Label>
                                    <DatePicker
                                        selected={form.eventDate}
                                        onChange={(date) => setForm(prev => ({ ...prev, eventDate: date }))}
                                        dateFormat="dd/MM/yyyy"
                                        placeholderText="Select date"
                                        minDate={new Date()}
                                        className="form-control"
                                        wrapperClassName="w-100"
                                    />
                                </Form.Group>

                                <Form.Group>
                                    <Form.Label className="ae-field-label">
                                        <Clock size={13} /> Event time <span className="text-muted fw-normal">(optional)</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="time"
                                        name="eventTime"
                                        value={form.eventTime}
                                        onChange={handleFormChange}
                                    />
                                    <p className="ae-field-hint mb-0">
                                        If left blank, the event triggers as soon as the date changes.
                                    </p>
                                </Form.Group>
                            </div>

                            {/* Section: Status (edit only) */}
                            {isEditing && (
                                <div className="ae-section">
                                    <div className="ae-section-title">Status</div>
                                    <Form.Group className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <div className="ae-field-label mb-1">
                                                <span className={`ae-dot ${form.eventStatus === 'ACTIVE' ? 'ae-dot-success' : 'ae-dot-danger'}`} />
                                                {form.eventStatus === 'ACTIVE' ? 'Active' : 'Inactive'}
                                            </div>
                                            <p className="ae-field-hint mb-0">
                                                Inactive events are kept but won't trigger on schedule.
                                            </p>
                                        </div>
                                        <Form.Check
                                            type="switch"
                                            id="event-status-switch"
                                            checked={form.eventStatus === 'ACTIVE'}
                                            onChange={(e) => setForm(prev => ({
                                                ...prev,
                                                eventStatus: e.target.checked ? 'ACTIVE' : 'INACTIVE',
                                            }))}
                                        />
                                    </Form.Group>
                                </div>
                            )}
                        </Form>

                        {/* Sticky footer actions */}
                        <div className="ae-panel-footer">
                            <Button
                                className="ae-btn-primary flex-grow-1 bg-primary"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving
                                    ? <><Loader2 size={14} className="me-2" style={{ animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />Saving…</>
                                    : isEditing ? 'Update event' : 'Create event'}
                            </Button>
                            <Button
                                variant="outline-secondary"
                                style={{ fontSize: '13px' }}
                                onClick={() => setShowPanel(false)}
                            >
                                Cancel
                            </Button>
                        </div>
                    </Offcanvas.Body>
                </Offcanvas>

            </div>
        </React.Fragment>
    );
};

export default AutomationEvents;